import { ColumnDef, SortingState } from "@tanstack/react-table";
import React, { useMemo } from "react";
import { DataTable } from "../../../components/data-table/DataTable";
import { useLocalization } from "../../../hooks/useLocalization";
import { formatDateYMD } from "../../../shared/utils/date";
import type { SeatHistoryEntry } from "./types";

/** Truncate a UUID string for display (e.g. "4b8a2cd1-49ef-4e5a-b6c3-1a7f…") */
const truncateUuid = (uuid: string, maxLen = 36): string => {
  if (uuid.length <= maxLen) return uuid;

  return `${uuid.slice(0, maxLen)}…`;
};

const INITIAL_SORTING: SortingState = [{ id: "dateAssigned", desc: true }];

interface SeatHistoryTableProps {
  data: SeatHistoryEntry[];
  isLoading?: boolean;
  onFilteredDataChange?: (rows: SeatHistoryEntry[]) => void;
}

export const SeatHistoryTable: React.FC<SeatHistoryTableProps> = ({
  data,
  isLoading = false,
  onFilteredDataChange,
}) => {
  const { t } = useLocalization();

  const columns = useMemo<ColumnDef<SeatHistoryEntry, any>[]>(
    () => [
      {
        id: "grantedViaLicense",
        header: t(
          "pages.dataAnalytics.userLevelData.seatHistory.columns.grantedViaLicense",
        ),
        accessorKey: "grantedViaLicense",
        cell: (ctx) => ctx.getValue<string>(),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: "arrIncludesSome",
        size: 100,
      },
      {
        id: "anonymizedUser",
        header: t(
          "pages.dataAnalytics.userLevelData.seatHistory.columns.anonymizedUser",
        ),
        accessorKey: "anonymizedUser",
        cell: (ctx) => truncateUuid(ctx.getValue<string>()),
        enableSorting: false,
        enableColumnFilter: false,
        size: 90,
      },
      {
        id: "dateAssigned",
        header: t(
          "pages.dataAnalytics.userLevelData.seatHistory.columns.dateAssigned",
        ),
        accessorFn: (row) => row.dateAssigned.getTime(),
        cell: (ctx) => formatDateYMD(new Date(ctx.getValue<number>())),
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "basic",
        size: 60,
      },
      {
        id: "currentExpiryDate",
        header: t(
          "pages.dataAnalytics.userLevelData.seatHistory.columns.currentExpiryDate",
        ),
        accessorFn: (row) => row.currentExpiryDate,
        cell: (ctx) => formatDateYMD(ctx.getValue<Date | undefined>()),
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "basic",
        size: 60,
      },
      {
        id: "originalExpiryDate",
        header: t(
          "pages.dataAnalytics.userLevelData.seatHistory.columns.originalExpiryDate",
        ),
        accessorFn: (row) => row.originalExpiryDate,
        cell: (ctx) => formatDateYMD(ctx.getValue<Date | undefined>()),
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "basic",
        size: 60,
      },
    ],
    [t],
  );

  return (
    <DataTable<SeatHistoryEntry>
      columns={columns}
      data={data}
      defaultSort={INITIAL_SORTING}
      facetedColumnIds={["grantedViaLicense"]}
      getRowId={(row) => row.id}
      isLoading={isLoading}
      onFilteredDataChange={onFilteredDataChange}
      showActionButton={false}
      tableKey="seatHistory"
      virtualize={true}
    />
  );
};
